import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { Combatant } from './simulation'
import { browserVfxCaps, resolveVfxQualityTier, type VfxRuntimeCaps } from './vfxRuntimePolicy'

export type FirstLightTransformationProfile = {
  particles: number
  lightScale: number
  motionScale: number
}

export function firstLightTransformationProfile(caps: VfxRuntimeCaps = {}): FirstLightTransformationProfile {
  const tier = resolveVfxQualityTier(caps)
  let particles = tier === 'ultra' ? 12 : tier === 'high' ? 9 : tier === 'balanced' ? 7 : 5
  let lightScale = tier === 'ultra' ? 1.08 : tier === 'high' ? 1 : tier === 'balanced' ? .82 : .66
  let motionScale = tier === 'ultra' ? 1.04 : tier === 'high' ? 1 : tier === 'balanced' ? .82 : .68
  if (caps.runtimePressure === 'high') {
    particles = Math.min(particles, 7)
    lightScale *= .82
    motionScale *= .78
  } else if (caps.runtimePressure === 'critical') {
    particles = Math.min(particles, 5)
    lightScale *= .62
    motionScale *= .55
  }
  if (caps.reducedMotion) {
    particles = Math.min(particles, 4)
    lightScale *= .72
    motionScale *= .28
  }
  return { particles: Math.max(3, particles), lightScale, motionScale }
}

export function FirstLightTransformationVfx({ state }: { state: Combatant }) {
  const root = useRef<THREE.Group>(null)
  const burst = useRef(0)
  const staticCaps = useMemo(() => browserVfxCaps(), [])
  const runtimePressure = typeof window === 'undefined'
    ? undefined
    : (window as typeof window & { __INYRON_RUNTIME_PERFORMANCE__?: { pressure?: 'normal' | 'high' | 'critical' } }).__INYRON_RUNTIME_PERFORMANCE__?.pressure
  const profile = firstLightTransformationProfile({ ...staticCaps, runtimePressure })

  useEffect(() => {
    if (state.form === 'first-light') burst.current = 1
  }, [state.form])

  useFrame((frame, dt) => {
    burst.current = Math.max(0, burst.current - Math.max(0, Math.min(.05, dt)) * .58)
    if (!root.current) return
    const t = frame.clock.elapsedTime
    const pulse = 1 + Math.sin(t * 5.2) * .055 * profile.motionScale
    root.current.rotation.y += Math.max(0, Math.min(.05, dt)) * .9 * profile.motionScale
    root.current.scale.setScalar(pulse + burst.current * .68 * profile.motionScale)
  })

  if (state.form !== 'first-light' && burst.current <= 0) return null
  const active = state.form === 'first-light'
  const strength = active ? 1 : .42

  return <group ref={root} name="jhadson-first-light-transformation" position={[0, 1.02, 0]}>
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      <torusGeometry args={[.72, .035, 8, 48]} />
      <meshBasicMaterial color="#ffd76a" transparent opacity={.68 * strength} blending={THREE.AdditiveBlending} depthWrite={false} />
    </mesh>
    <mesh rotation={[0, 0, Math.PI / 2]}>
      <torusGeometry args={[.9, .018, 6, 48]} />
      <meshBasicMaterial color="#ffffff" transparent opacity={.38 * strength} blending={THREE.AdditiveBlending} depthWrite={false} />
    </mesh>
    <mesh scale={[1.08, 1.42, 1.08]}>
      <sphereGeometry args={[.54, 22, 18]} />
      <meshBasicMaterial color="#fff3b0" transparent opacity={.065 * strength} blending={THREE.AdditiveBlending} depthWrite={false} />
    </mesh>
    {Array.from({ length: profile.particles }, (_, i) => {
      const a = i / profile.particles * Math.PI * 2
      const r = .62 + (i % 3) * .13
      return <mesh key={i} position={[Math.cos(a) * r, (i % 4 - .8) * .23, Math.sin(a) * r]} rotation={[a, a * .5, -a]}>
        <octahedronGeometry args={[.035 + (i % 3) * .012, 0]} />
        <meshBasicMaterial color={i % 3 === 0 ? '#ffffff' : i % 2 ? '#ffd76a' : '#9edbff'} transparent opacity={.74 * strength} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    })}
    <pointLight color="#ffd76a" intensity={6.4 * strength * profile.lightScale} distance={5.2} decay={2} />
    <pointLight color="#9edbff" intensity={2.8 * strength * profile.lightScale} distance={4} decay={2} position={[0, .8, 0]} />
  </group>
}
